import {
  DefaultButton, Dialog, DialogFooter, DialogType, IconButton, mergeStyleSets, Stack, TextField,
} from '@fluentui/react';
import React, { useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import Theme from '../Theme';
import IconButtonCustom from '../IconButton';
import Utils from '../Utils';
import './JsonViewer.css';

const componentStyles = mergeStyleSets({
  header: {
    width: '100%',
    minWidth: '100%',
    maxWidth: '100%',
  },

  iconButton: {
    selectors: {
      '&:active': {
        backgroundColor: 'transparent !important',
      },
      i: {
        fontSize: '25px',
      },
    },
  },
});

export default ({ json, setJson }) => {
  const scrollYRef = useRef(0);
  const bodyStylesRef = useRef({});

  useEffect(() => {
    if (json) {
      // Store current scroll position and body styles
      scrollYRef.current = window.scrollY;
      bodyStylesRef.current = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
      };

      // Apply position-lock pattern
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = '100%';
    } else {
      // Restore previous styles and scroll position
      document.body.style.position = bodyStylesRef.current.position || '';
      document.body.style.top = bodyStylesRef.current.top || '';
      document.body.style.width = bodyStylesRef.current.width || '';
      window.scrollTo(0, scrollYRef.current);
    }

    // Cleanup on unmount
    return () => {
      if (json) {
        document.body.style.position = bodyStylesRef.current.position || '';
        document.body.style.top = bodyStylesRef.current.top || '';
        document.body.style.width = bodyStylesRef.current.width || '';
        window.scrollTo(0, scrollYRef.current);
      }
    };
  }, [json]);

  return (
    <Dialog
      hidden={!json}
      onDismiss={() => { setJson(); }}
      dialogContentProps={{
        type: DialogType.normal,
        title: (
          <Stack horizontal className={componentStyles.header}>

            <Stack horizontal horizontalAlign="start">
              <header>{`Full Record Entry: ${json?.uuid}`}</header>
              <IconButtonCustom
                iconName="Installation"
                tooltip="Download full record"
                className={componentStyles.iconButton}
                styles={{
                  container: {
                    lineHeight: '40px !important',
                    marginLeft: '10px !important',
                  },
                }}
                onClick={() => {
                  if (!json) {
                    return;
                  }
                  const content = JSON.stringify(json);
                  Utils.saveToFile(`${json.uuid}.json`, content);
                }}
              />
              {(json?.inputs) && (
              <IconButtonCustom
                iconName="Download"
                tooltip="Download inputs"
                className={componentStyles.iconButton}
                styles={{
                  container: {
                    lineHeight: '40px !important',
                    marginLeft: '10px !important',
                  },
                }}
                onClick={() => {
                  if (!json?.inputs) {
                    return;
                  }
                  const content = JSON.stringify(json.inputs);
                  Utils.saveToFile(`${json.uuid}-inputs.json`, content);
                }}
              />
              )}
            </Stack>

            <Stack grow={100} />

            <Stack horizontal horizontalAlign="end">
              <IconButton
                className={Theme.styles.outlineControlButton}
                iconProps={{ iconName: 'Cancel' }}
                aria-label="Close"
                onClick={() => { setJson(); }}
              />
            </Stack>
          </Stack>
        ),
        closeButtonAriaLabel: 'Close',
        styles: {
          title: {
            paddingRight: '14px',
          },
          subText: {
            marginBottom: '5px',
          },
        },
      }}
      modalProps={{
        isBlocking: false,
        containerClassName: 'json-dialog-open',
        layerProps: { hostId: undefined },
        allowTouchBodyScroll: true,
        styles: {
          main: {
            width: isMobile ? '95vw !important' : '80vw !important',
            maxWidth: isMobile ? '95vw !important' : 'min(1200px, 90vw) !important',
            minWidth: 'auto !important',
            flex: '0 1 auto !important',
          },
          innerContent: {
            maxHeight: '80vh',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
          },
        },
      }}
    >

      <Stack horizontal>
        <Stack.Item grow styles={{ root: { maxHeight: '71vh', overflow: 'auto' } }}>
          <TextField
            readOnly
            multiline
            rows={10}
            styles={{
              field: {
                height: isMobile ? '57vh' : '70vh',
                width: isMobile ? '100%' : '70vw',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                overscrollBehavior: 'contain',
              },
              wrapper: {
                height: isMobile ? '57vh' : '70vh',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                overscrollBehavior: 'contain',
              },
              fieldGroup: {
                height: isMobile ? '57vh' : '70vh',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                overscrollBehavior: 'contain',
              },
            }}
            value={JSON.stringify(json || {}, null, 4)}
          />
        </Stack.Item>
      </Stack>

      <DialogFooter>

        <DefaultButton
          onClick={() => {
            setJson();
          }}
          text="Close"
        />
      </DialogFooter>
    </Dialog>
  );
};
